import { MutableRefObject, ReactNode, useRef } from 'react';
import { createPortal } from 'react-dom';

import Button from '@components/button/Button';
import Icon from '@components/icon/Icon';
import testId from '@constants/testId';
import useOnClickOutside from '@utils/hooks';

interface ModalProps {
  title: string;
  /** Shown as a rounded badge next to the title, e.g. a result count. */
  count?: number;
  description?: string;
  onClose: () => void;
  children: ReactNode;
  className?: string;
  primaryButtonLabel?: string;
  primaryButtonClick?: () => void;
  primaryButtonDisabled?: boolean;
  primaryButtonLoading?: boolean;
  secondaryButtonLabel?: string;
  secondaryButtonClick?: () => void;
  secondaryButtonDisabled?: boolean;
  isClickOutsideRequired?: boolean;
  primaryButtonStyle?: string;
  secondaryButtonStyle?: string;
  infoText?: ReactNode;
  bodyClassName?: string;
  showCloseButton?: boolean;
  onHeaderBack?: () => void;
  headerClassName?: string;
  descriptionClassOverride?: string;
  titleClassName?: string;
  footerWrapperClassName?: string;
  overlayClassName?: string;
}

const Modal = ({
  title,
  count,
  description,
  onClose,
  children,
  className = '',
  primaryButtonLabel,
  primaryButtonClick,
  primaryButtonDisabled = false,
  primaryButtonLoading = false,
  secondaryButtonLabel,
  secondaryButtonClick,
  secondaryButtonDisabled = false,
  isClickOutsideRequired = true,
  infoText,
  primaryButtonStyle,
  secondaryButtonStyle,
  bodyClassName = '',
  showCloseButton = true,
  onHeaderBack,
  headerClassName = '',
  descriptionClassOverride = '',
  titleClassName = '',
  footerWrapperClassName = '',
  overlayClassName = ''
}: ModalProps) => {
  const modalRef = useRef() as MutableRefObject<HTMLDivElement>;

  useOnClickOutside(modalRef, (e) => {
    if (isClickOutsideRequired && onClose) {
      e.stopPropagation();
      onClose();
    }
  });

  const hasFooterButtons = primaryButtonLabel || secondaryButtonLabel;

  const modalUi = (
    <div
      className={`fixed left-0 top-0 z-[60] flex size-full items-center justify-center ${overlayClassName}`}
      data-modal-overlay
    >
      {/* Modal Overlay */}
      <div className="absolute size-full bg-black opacity-30" />

      {/* Modal Main Content */}
      <div
        className={`relative mx-auto flex max-h-[98%] w-120 flex-col overflow-hidden rounded-2xl bg-cofeeNeutral1 ${className}`}
        ref={modalRef}
      >
        {/* Header */}
        <div
          className={`relative border-b border-cofeeGreyBlue5 px-6 py-4 ${headerClassName}`}
        >
          <div
            className={`flex items-start gap-3 ${
              showCloseButton ? 'pr-10' : ''
            }`}
          >
            {onHeaderBack && (
              <Button
                variant="transparent"
                handleClick={onHeaderBack}
                className="!mt-0.5 !size-6 shrink-0 !p-0"
                data-testid={testId.modal.modalBack}
              >
                <Icon name="arrow-left" size="24" />
              </Button>
            )}
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <h2
                  className={`text-xl font-semibold text-content ${
                    titleClassName || ''
                  }`}
                >
                  {title}
                </h2>
                {typeof count === 'number' && (
                  <div className="rounded-full bg-cofeeNeutral4 px-2.5 py-0.5 text-base font-medium text-cofeeNeutral10">
                    {count}
                  </div>
                )}
              </div>

              {description && (
                <p
                  className={`mt-1 text-sm text-grey ${descriptionClassOverride}`}
                >
                  {description}
                </p>
              )}
            </div>
          </div>
          {showCloseButton && (
            <Button
              variant="transparent"
              handleClick={onClose}
              className="absolute right-6 top-4 !size-6 !p-0"
              data-testid={testId.modal.modalClose}
            >
              <Icon
                name="close-custom-color"
                size="24"
                className="text-cofeeNeutral8"
              />
            </Button>
          )}
        </div>
        {/* Body */}
        <div
          className={`min-h-0 flex-1 overflow-y-auto px-6 py-4 ${bodyClassName}`}
        >
          {children}
        </div>

        {/* Footer */}
        {hasFooterButtons && (
          <div
            className={`flex items-center border-t border-cofeeGreyBlue5 px-6 py-4 ${
              infoText ? 'justify-between' : 'justify-end'
            } ${footerWrapperClassName}`}
          >
            {infoText}
            <div className="flex items-center justify-end gap-3 ">
              {secondaryButtonLabel && (
                <Button
                  variant="tertiary"
                  label={secondaryButtonLabel}
                  handleClick={secondaryButtonClick}
                  disabled={secondaryButtonDisabled}
                  className={`!min-w-15 ${secondaryButtonStyle}`}
                />
              )}
              {primaryButtonLabel && (
                <Button
                  variant="primary"
                  label={primaryButtonLabel}
                  handleClick={primaryButtonClick}
                  disabled={primaryButtonDisabled}
                  loading={primaryButtonLoading}
                  className={`!min-w-15 ${primaryButtonStyle}`}
                />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(modalUi, document.body);
};

export default Modal;
